import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  Send,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  Loader2,
} from "lucide-react";
import {
  getTelegramStatus,
  registerTelegramWebhook,
} from "@/lib/api/telegram-admin.functions";

export const Route = createFileRoute("/_authenticated/portal/admin/telegram")({
  component: AdminTelegramPage,
});

function AdminTelegramPage() {
  const status = useServerFn(getTelegramStatus);
  const register = useServerFn(registerTelegramWebhook);

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["telegram-status"],
    queryFn: () => status({}),
  });

  const registerMutation = useMutation({
    mutationFn: () => register({}),
    onSuccess: (res: any) => {
      toast.success(
        res?.botUsername
          ? `Webhook registered for @${res.botUsername}`
          : "Webhook registered",
      );
      refetch();
    },
    onError: (e: any) =>
      toast.error("Could not register the webhook", {
        description: e?.message ?? String(e),
      }),
  });

  const connected = (data as any)?.connected;
  const botUsername = (data as any)?.botUsername as string | undefined;
  const webhookUrl = (data as any)?.webhookUrl as string | undefined;
  const expectedUrl = (data as any)?.expectedUrl as string | undefined;
  const lastError = (data as any)?.lastError as string | null | undefined;
  const healthy = connected && webhookUrl && webhookUrl === expectedUrl;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl text-navy">Telegram bot</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Clients can browse services and book consultations without leaving
          Telegram. Bookings land in the same pipeline as the website — same
          invoice, SMS and email confirmations.
        </p>
      </header>

      <section className="rounded-2xl border border-border bg-card p-6 shadow-soft">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-lg text-navy">Connection status</h2>
            {isFetching ? (
              <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Checking…
              </p>
            ) : !connected ? (
              <p className="mt-2 flex items-start gap-2 text-sm text-amber-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                Telegram isn't connected yet. Ask Lovable to link the Telegram
                connector, then come back and register the webhook.
              </p>
            ) : (
              <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                <li>
                  Bot:{" "}
                  {botUsername ? (
                    <a
                      className="text-navy underline"
                      href={`https://t.me/${botUsername}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      @{botUsername} <ExternalLink className="inline h-3 w-3" />
                    </a>
                  ) : (
                    "unknown"
                  )}
                </li>
                <li className="break-all">
                  Webhook: {webhookUrl || <em>not registered</em>}
                </li>
                <li>Pending updates: {(data as any)?.pendingUpdates ?? 0}</li>
                {lastError && (
                  <li className="text-destructive">Last error: {lastError}</li>
                )}
              </ul>
            )}
          </div>
          {healthy && (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-800">
              <CheckCircle2 className="h-3.5 w-3.5" /> Live
            </span>
          )}
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => registerMutation.mutate()}
            disabled={registerMutation.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-navy-foreground hover:bg-navy/90 disabled:opacity-60"
          >
            {registerMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Register / refresh webhook
          </button>
          <button
            type="button"
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-medium hover:border-navy/40"
          >
            <RefreshCw className="h-4 w-4" /> Refresh status
          </button>
        </div>
      </section>

      <section className="rounded-2xl border border-border bg-card p-6 shadow-soft">
        <h2 className="font-display text-lg text-navy">What clients can do</h2>
        <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
          <li>
            <code>/start</code> — main menu
          </li>
          <li>
            <code>/book</code> — guided booking: service → meeting type → date →
            live free slots → details → confirm, then an invoice link
          </li>
          <li>
            <code>/services</code> — full service list
          </li>
          <li>
            <code>/mybookings</code> — their recent bookings
          </li>
          <li>
            <code>/link</code> — connect their website account so details
            pre-fill and history is complete
          </li>
        </ul>
      </section>
    </div>
  );
}
